import { FC, useEffect, useMemo, useState } from "react";
import { cn } from "@/utils/utils";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import littleGuy from "../../TDD-Images/little-guy-alone.png";
import { Clock } from "lucide-react";
import { useAppContext } from "@/appContext";
import CodeMirror from "@uiw/react-codemirror";
import { OrgDetailsType } from "@/utils/feedbackBadges";
import { InfoOutlined } from "@mui/icons-material";
import { BiteSizeOption } from "@/utils/types";
import Tooltip from "@mui/material/Tooltip";
import ToolTip from "./ToolTip";
import CustomTooltip from "./CustomTooltip";

interface Props {
  time: string;
  timerOn?: boolean;
  options?: BiteSizeOption[];
  org?: OrgDetailsType;
  isInterview?: boolean;
  newQuestionClick: (ind: number) => void;
  handleOptionClick?: (userresp: string, resp: string) => void;
}

const Question: FC<Props> = ({
  time,
  org,
  isInterview,
  newQuestionClick,
  timerOn,
  options,
  handleOptionClick,
}) => {
  const [whiteBoardSwitch, setWhiteBoardSwitch] = useState(false);
    const {
    state,
    startFunc,
    reducers
  } = useAppContext();
  const [showWhiteboardTooltip, setShowWhiteboardTooltip] = useState(
    state.isPractice && state.practiceStep === -1
  );


  const showWhiteBoard = useMemo(() => {
    if (isInterview && whiteBoardSwitch) return true;
    if (!isInterview && state.session[state.currentQuestion]?.code) return true;
    return false;
  }, [
    whiteBoardSwitch,
    state.session[state.currentQuestion]?.code,
    isInterview,
  ]);

  useEffect(() => { }, [state]);

  // When whiteboard is clicked, hide tooltip and start InterviewButtons flow
  const handleWhiteboardClick = () => {
    reducers.setuseWhiteboard(!whiteBoardSwitch)
    setWhiteBoardSwitch((s) => !s);
    if (showWhiteboardTooltip) {
      setShowWhiteboardTooltip(false);
      // Start InterviewButtons tooltip flow
      if (state.isPractice && state.practiceStep === -1) {
        reducers.setpracticeStep(0); // Start at video tooltip
      }
    }
  };

  return (
    <>
      <div className="flex flex-col py-[30px] px-[24px] gap-2 border-b-2 relative">
        <div className="flex justify-between flex-wrap items-center">
          <div className="flex gap-4">
            {state.session.map((q, index) => (
              <Badge
                key={index}
                variant={"secondary"}
                onClick={() => {
                  newQuestionClick(index);
                }}
                className={cn(
                  "cursor-pointer rounded-full",
                  state.currentQuestion === index
                    ? "bg-gray-100"
                    : "bg-white border-[0.5px] border-gray-300"
                )}
              >
                Question {index + 1}
              </Badge>
            ))}
          </div>
          <Badge
            variant={"secondary"}
            className={`${timerOn ? "bg-[#757eb6]" : "bg-[#F5F5F5]"
              } rounded-full gap-1`}
          >
            <Clock className="w-4" />
            {time}
          </Badge>
        </div>
        <div className="text-left p-0 m-0 py-2">
          <p className="font-semibold text-[24px]">
            {state.session.at(state.currentQuestion)?.question.question}
          </p>
        </div>
        {/* {state.isPractice &&
          <ToolTip
            message="This is a question from the interviewer."
            className="-bottom-[90px] left-1/2 transform -translate-x-1/2"
          />
        } */}
        <div className="flex gap-4 z-[60]">
          {isInterview && !options && (
            <div className="text-sm flex flex-col gap-2 relative">
              <div  className="flex gap-2 ">
              <Checkbox
                className="border-black drop-shadow-none"
                checked={whiteBoardSwitch}
                onCheckedChange={handleWhiteboardClick}
              />
              <label className="p-0 m-0">Use Whiteboard</label>
              </div>
              {showWhiteboardTooltip && (
                <CustomTooltip arrowDirection="up">
                  Enable the whiteboard for drawing or writing code.
                </CustomTooltip>
              )}
            </div>
          )}
          {!state.isAssessment &&!state.isPractice && org && org.inOrg && !options && (
            <div className="text-sm flex gap-2 ">
              <Checkbox
                className="border-black drop-shadow-none"
                checked={org.accessibleToOrg}
                onCheckedChange={() =>
                  org.accessibleToOrg &&
                  org.updateAccessibleToOrg &&
                  org.updateAccessibleToOrg(!org.accessibleToOrg)
                }
              />
              <label className="p-0 m-0">Accessible to Organization</label>
            </div>
          )}
        </div>
      </div>
      {options && (
        <div className="flex-col w-[100%] my-4 ">
          <div className="flex justify-start flex-wrap gap-y-2">
            {options.map((option, index) => (
              <Tooltip key={index} title={option.info} arrow>
                <div
                  className="w-[46%] bg-whiteButton hover:bg-whiteButton-hover rounded-lg shadow-md  mx-2 p-2  flex text-grayBlue cursor-pointer"
                  onClick={() =>
                    handleOptionClick!(option.value, option.response)
                  }
                >
                  <div className="p-2 mr-3 flex self-center">
                    {option.info && <InfoOutlined />}
                  </div>
                  <div className="flex-col justify-center items-center text-left">
                    <p className="lg:text-xl text-xs sm:text-xs md:text-lg font-semibold">
                      {option.label}
                    </p>
                  </div>
                </div>
              </Tooltip>
            ))}
          </div>
        </div>
      )}
      {showWhiteBoard && (
        <CodeMirror
          value={state.session[state.currentQuestion]?.code}
          height="50vh"
          width="85%"
          editable={isInterview}
          className="pt-8"
          onChange={(value) => reducers.updatesession({ code: value })}
        />
      )}
      <div className="fixed bottom-0 left-0 w-[110px]">
        <img src={littleGuy} alt="Buddy" className="w-[110px]" />
      </div>
    </>
  );
};

export default Question;
